import React, { useEffect, useState } from "react";
import { Bot, CheckCircle2, XCircle, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { agentsApi, formatApiError } from "../../lib/ledgerApi";

export default function AgentApprovals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = async () => {
    try {
      const r = await agentsApi.approvals();
      setItems(r);
    } catch (e) {
      toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, []);

  const onApprove = async (id) => {
    setActing(id);
    try {
      const r = await agentsApi.approveIntent(id);
      toast.success(`Intent ${r.status}`, {
        description: r.receipt
          ? `Receipt ${r.receipt.receipt_id.slice(0, 12)}…`
          : null,
      });
      await load();
    } catch (e) {
      toast.error("Approve failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setActing(null);
    }
  };

  const onReject = async (id) => {
    setActing(id);
    try {
      await agentsApi.rejectIntent(id);
      toast.success("Intent rejected");
      await load();
    } catch (e) {
      toast.error("Reject failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="max-w-5xl px-6 md:px-10 py-10 md:py-14" data-testid="agent-approvals-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
            / Agent approvals
          </div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-medium flex items-center gap-3">
            <Bot className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
            Pending agent payments
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Payments that exceeded the agent's <code className="font-mono">require_human_approval_above_usd</code>
            threshold. Approving runs them through fraud + compliance and settles atomically.
          </p>
        </div>
        <Button onClick={load} variant="outline" data-testid="refresh-approvals" className="rounded-sm">
          <RefreshCw className="w-3 h-3 mr-1.5" strokeWidth={1.5} /> Refresh
        </Button>
      </div>

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="border border-border rounded-sm bg-card p-10 text-center text-sm text-muted-foreground" data-testid="approvals-empty">
          No pending agent payments.
        </div>
      )}

      <div className="space-y-3">
        {items.map((it) => (
          <div
            key={it.id}
            data-testid={`approval-row-${it.id}`}
            className="border border-border rounded-sm bg-card p-5"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-1.5 py-0.5 border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 rounded-sm text-[10px] uppercase tracking-widest font-mono">
                    {it.policy_verdict?.code || "REQUIRES_HUMAN_APPROVAL"}
                  </span>
                  <span className="text-[10px] text-muted-foreground font-mono">
                    intent {it.id.slice(0, 12)}…
                  </span>
                </div>
                <div className="text-[11px] text-muted-foreground font-mono mb-1">
                  {it.agent_did}
                </div>
                <div className="text-sm">
                  <span className="font-mono text-base">${it.amount}</span>{" "}
                  <span className="text-muted-foreground text-xs">{it.currency}</span>
                  <span className="text-muted-foreground"> → </span>
                  <span className="font-mono">{it.to_vpa}</span>
                </div>
                {it.description && (
                  <div className="text-xs text-muted-foreground mt-1">{it.description}</div>
                )}
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px] font-mono">
                  <div>
                    <span className="text-muted-foreground">USD ≈ </span>${it.approx_usd}
                  </div>
                  <div>
                    <span className="text-muted-foreground">category </span>{it.category || "—"}
                  </div>
                  <div>
                    <span className="text-muted-foreground">signed by </span>{it.signed_by}
                  </div>
                  <div>
                    <span className="text-muted-foreground">created </span>
                    {new Date(it.created_at).toLocaleTimeString()}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="outline"
                  onClick={() => onReject(it.id)}
                  disabled={acting === it.id}
                  data-testid={`reject-${it.id}`}
                  className="rounded-sm"
                >
                  {acting === it.id
                    ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    : <XCircle className="w-3 h-3 mr-1" strokeWidth={1.5} />}
                  Reject
                </Button>
                <Button
                  onClick={() => onApprove(it.id)}
                  disabled={acting === it.id}
                  data-testid={`approve-${it.id}`}
                  className="rounded-sm"
                >
                  {acting === it.id
                    ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    : <CheckCircle2 className="w-3 h-3 mr-1" strokeWidth={1.5} />}
                  Approve
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
